import type { StoryCtx } from './ctx.js';
import { draftOf, narrativeOrder } from './narrative.js';
import { isScene } from './scene.js';
import { normalizeSceneTime } from './sceneTime.js';
import { nodeTimes } from './timeline.js';
import { isDuration, isRelativeMark, markMoment, momentOf, momentText, timeFromMarks, timeMarksIn, type Moment } from './timeMarks.js';
import type { SceneTime, StoryNode, TimePrecision } from './types.js';

// ---------------------------------------------------------------------------
// Zeit aus dem Text und relative Zeit (PLAN.md §16): Die Zeitmarken im Draft einer Szene bestimmen ihre Zeit; relative Angaben
// („drei Tage später“) gehen von einer Ankerszene aus – vorgegeben der vorigen Szene in Lesereihenfolge, die eine Zeit hat. Der Anker
// wird beim ersten Mal festgehalten, damit Umsortieren die Zeit nicht verschiebt; verschiebt sich die Ankerszene, ziehen die abhängigen
// Szenen nach (`refreshedTime`, der Dienst geht die Kette entlang).
// ---------------------------------------------------------------------------

/** Vorgabe-Anker: die vorige Szene in Lesereihenfolge, die eine Zeit hat (null: keine, bzw. die Szene ist nicht eingeordnet). */
export function defaultAnchor(ctx: StoryCtx, scene: StoryNode): StoryNode | null {
  const order = narrativeOrder(ctx).scenes.map((s) => s.scene);
  const i = order.findIndex((s) => s.id === scene.id);
  for (let j = i - 1; j >= 0; j--) if (nodeTimes(ctx, order[j]!).start) return order[j]!;
  return null;
}

/** Zeitpunkt, von dem relative Angaben ausgehen: das Ende bzw. der Beginn der Ankerszene (null ohne Zeit). */
export function anchorMoment(ctx: StoryCtx, anchorId: string): Moment | null {
  const a = ctx.nodes.get(anchorId);
  if (!a || !isScene(a)) return null;
  const t = nodeTimes(ctx, a);
  return momentOf(t.end ?? t.start);
}

/** Führt die Kette der Anker von `anchorId` aus zurück zu `sceneId`? Dann wäre `anchorId` als Anker von `sceneId` ein Kreis. */
export function anchorCycle(ctx: StoryCtx, sceneId: string, anchorId: string): boolean {
  const seen = new Set<string>();
  for (let id: string | undefined = anchorId; id; id = ctx.nodes.get(id)?.time?.anchor?.scene) {
    if (id === sceneId) return true;
    if (seen.has(id)) return false;
    seen.add(id);
  }
  return false;
}

/** Taugt `anchorId` als Anker von `scene`? (eine andere Szene, kein Kreis) */
export const validAnchor = (ctx: StoryCtx, scene: StoryNode, anchorId: string): boolean => {
  const a = ctx.nodes.get(anchorId);
  return !!a && isScene(a) && a.id !== scene.id && !anchorCycle(ctx, scene.id, anchorId);
};

/** Anker, von dem relative Zeitmarken im Text einer Szene ausgehen: der gespeicherte (falls es ihn noch gibt), sonst die vorige Szene in Lesereihenfolge */
export function textAnchorOf(ctx: StoryCtx, scene: StoryNode): string | null {
  const stored = scene.time?.anchor?.scene;
  return stored && validAnchor(ctx, scene, stored) ? stored : (defaultAnchor(ctx, scene)?.id ?? null);
}

/**
 * Zeit einer Szene aus den Zeitmarken ihres Drafts (Herkunft „Text“). Relative Marken gehen vom Anker aus: dem angegebenen (`anchorId`;
 * null = keiner), sonst dem gespeicherten (falls es ihn noch gibt), sonst der vorigen Szene in Lesereihenfolge. Hing der Beginn am
 * Anker, steht er in der Zeit. null, wenn der Text keine bestimmbare Zeitmarke hat.
 */
export function textTimeOf(ctx: StoryCtx, scene: StoryNode, text: string = draftOf(scene), anchorId?: string | null): SceneTime | null {
  const marks = timeMarksIn(text);
  if (marks.length === 0) return null;
  const chosen = anchorId !== undefined ? anchorId : textAnchorOf(ctx, scene);
  const anchor = chosen && validAnchor(ctx, scene, chosen) ? chosen : null;
  const r = timeFromMarks(marks, anchor ? anchorMoment(ctx, anchor) : null);
  if (!r.time) return null;
  return normalizeSceneTime({ ...r.time, ...(r.usesBase && anchor ? { anchor: { scene: anchor } } : {}) });
}

/**
 * Zeit mit festem Abstand zur Ankerszene (von Hand, `anchor.offset`): Beginn = Zeit des Ankers + Abstand; ein Zeitraum behält seine
 * Dauer. null, wenn der Anker keine Zeit hat bzw. der Abstand nicht zu ihr passt.
 */
export function offsetTimeOf(ctx: StoryCtx, scene: StoryNode, time: SceneTime): SceneTime | null {
  const anchor = time.anchor;
  if (!anchor?.offset || !validAnchor(ctx, scene, anchor.scene)) return null;
  const start = markMoment(anchor.offset, anchorMoment(ctx, anchor.scene));
  if (!start) return null;
  let end: Moment | null = null;
  const a = momentOf(time.start);
  const b = momentOf(time.end);
  if (!time.duration && a && b && a.basis === b.basis && a.basis === start.basis && b.value >= a.value) end = { ...start, value: start.value + (b.value - a.value) };
  return normalizeSceneTime({ ...time, start: momentText(start), end: end ? momentText(end) : null });
}

/** Szenen, deren Zeit an der Szene `sceneId` hängt (sie ist ihr Anker) */
export const dependentsOf = (ctx: StoryCtx, sceneId: string): StoryNode[] => ctx.story.nodes.filter((n) => isScene(n) && n.time?.anchor?.scene === sceneId);

const same = (a: SceneTime | null | undefined, b: SceneTime | null | undefined): boolean => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/**
 * Zeit einer abhängigen Szene neu bestimmen, nachdem sich ihr Anker verschoben hat: aus ihrem Text bzw. mit festem Abstand. null, wenn
 * sie sich nicht ändert oder nicht bestimmen lässt.
 */
export function refreshedTime(ctx: StoryCtx, scene: StoryNode): SceneTime | null {
  const t = scene.time;
  if (!t?.anchor) return null;
  const next = t.anchor.offset ? offsetTimeOf(ctx, scene, t) : t.source === 'text' ? textTimeOf(ctx, scene) : null;
  return next && !same(next, t) ? next : null;
}

/**
 * Was eine Zeitmarke an einer Stelle des Drafts für die Szene ergibt (Vorschau beim Setzen): der Zeitpunkt – mit den Marken davor und
 * dem Anker, wie `textTimeOf` rechnet – oder warum keiner. `anchorId`: der Zeitpunkt hängt an dieser Ankerszene.
 * Gründe: `yearless` – Tag ohne Jahr, aber keine Szene davor mit Datum; `relative` – relativ, aber nichts davor mit Zeit;
 * `basis` – die Szene davor zählt eigene Einheiten (bzw. hat ein Datum), die Angabe passt nicht dazu; `duration` – eine Dauer, kein Zeitpunkt.
 */
export type MarkPreview = { moment: Moment; anchorId: string | null } | { moment: null; reason: 'yearless' | 'relative' | 'basis' | 'duration' };

export function previewMark(ctx: StoryCtx, scene: StoryNode, marksBefore: readonly string[], value: string): MarkPreview {
  if (isDuration(value)) return { moment: null, reason: 'duration' };
  const chosen = textAnchorOf(ctx, scene);
  const base = chosen ? anchorMoment(ctx, chosen) : null;
  let cursor: Moment | null = null;
  for (const v of marksBefore) {
    if (isDuration(v)) continue;
    cursor = markMoment(v, isRelativeMark(v) ? (cursor ?? base) : null) ?? cursor;
  }
  const from = isRelativeMark(value) ? (cursor ?? base) : null;
  const moment = markMoment(value, from);
  if (moment) return { moment, anchorId: from && !cursor && from === base ? chosen : null };
  if (!isRelativeMark(value)) return { moment: null, reason: 'basis' };
  if (!from) return { moment: null, reason: /^--/.test(value) ? 'yearless' : 'relative' };
  return { moment: null, reason: 'basis' };
}

/** Derselbe Zeitpunkt in der Genauigkeit `p`? (ein Datum bis aufs Jahr, den Monat …; eine Zählung in Schritten dieser Größe) */
export function sameAt(a: Moment, b: Moment, p: TimePrecision): boolean {
  if (a.basis !== b.basis) return false;
  if (a.basis === 'ms') {
    const n = { year: 4, month: 7, day: 10, hour: 13, minute: 16 }[p];
    return momentText(a).slice(0, n) === momentText(b).slice(0, n);
  }
  const step = { year: 525960, month: 43200, day: 1440, hour: 60, minute: 1 }[p];
  return Math.floor(a.value / step) === Math.floor(b.value / step);
}

/**
 * Passt die Zeit einer Szene nicht mehr zu den Zeitmarken ihres Drafts? Das kommt vor, wenn sie danach auf der Zeitlinie gezogen oder
 * von Hand gesetzt wurde (Herkunft nicht „Text“; der Anker hat sich dabei gelöst, PLAN.md §16). Verglichen wird in der Genauigkeit der
 * Marke: der Beginn und, wenn der Text eines ergibt, das Ende. Liefert die Zeit laut Text, wenn sie abweicht, sonst null.
 */
export function textTimeMismatch(ctx: StoryCtx, scene: StoryNode): SceneTime | null {
  const t = scene.time;
  if (!isScene(scene) || !t?.start || t.source === 'text') return null;
  const text = textTimeOf(ctx, scene);
  const a = momentOf(text?.start);
  const b = momentOf(t.start);
  if (!text || !a || !b) return null;
  const p = text.precision ?? 'minute';
  if (!sameAt(a, b, p)) return text;
  const c = momentOf(text.end);
  const d = momentOf(t.end);
  return c && (!d || !sameAt(c, d, p)) ? text : null;
}

/** Haben sich die Zeitmarken eines Textes geändert (Werte, Rolle, Genauigkeit, ungefähr – nicht die Formulierung)? */
export const marksChanged = (before: string, after: string): boolean => {
  const sig = (t: string): string => JSON.stringify(timeMarksIn(t).map((m) => [m.value, m.precision, m.approx, m.role]));
  return sig(before) !== sig(after);
};
